import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  cn,
  Icon,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@qw/ui";
import { createContext, type FC, type HTMLAttributes, type PropsWithChildren, useContext, useState } from "react";

/**
 * Whether the enclosing tool invocation's disclosure is open. {@link ChatMessageToolInvocationContent}
 * reads it so a collapsed payload is never built: the transcript keeps every historical invocation
 * mounted, and serializing each one's input/output on every render made streaming cost grow with
 * history length even though none of that text was on screen.
 */
const ToolInvocationOpenContext = createContext(false);

export interface ChatMessageToolInvocationProps extends HTMLAttributes<HTMLDivElement> {}

export const ChatMessageToolInvocation: FC<PropsWithChildren<ChatMessageToolInvocationProps>> = ({
  children,
  className,
  ...props
}) => {
  // Controlled (rather than letting Radix own the state internally) purely so the open flag is
  // readable by the content slot below. The trigger, the `data-state` attributes, and the
  // default-closed behavior are unchanged.
  const [isOpen, setIsOpen] = useState(false);

  return (
    <ToolInvocationOpenContext.Provider value={isOpen}>
      <Collapsible asChild={true} open={isOpen} onOpenChange={setIsOpen}>
        <div {...props} className={cn("w-full max-w-full flex flex-col text-muted-foreground", className)}>
          {children}
        </div>
      </Collapsible>
    </ToolInvocationOpenContext.Provider>
  );
};

export interface ChatMessageToolInvocationHeaderProps extends HTMLAttributes<HTMLElement> {}

export const ChatMessageToolInvocationHeader: FC<PropsWithChildren<ChatMessageToolInvocationHeaderProps>> = ({
  children,
  className,
  ...props
}) => {
  return (
    <CollapsibleTrigger asChild>
      <header {...props} className={cn("flex items-center gap-2 cursor-pointer group", className)}>
        {children}
      </header>
    </CollapsibleTrigger>
  );
};

export interface ChatMessageToolInvocationTitleProps extends HTMLAttributes<HTMLHeadingElement> {
  tooltip?: string;
}

export const ChatMessageToolInvocationTitle: FC<PropsWithChildren<ChatMessageToolInvocationTitleProps>> = ({
  children,
  className,
  tooltip,
  ...props
}) => {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <h3 {...props} className={cn("gap-2 truncate text-sm font-normal tracking-normal", className)}>
          {children}
        </h3>
      </TooltipTrigger>
      <TooltipContent hidden={!tooltip}>{tooltip}</TooltipContent>
    </Tooltip>
  );
};

export interface ChatMessageToolInvocationContentProps extends HTMLAttributes<HTMLDivElement> {
  content: any;
  children?: React.ReactNode;
}

export const ChatMessageToolInvocationContent: FC<ChatMessageToolInvocationContentProps> = ({
  content,
  children,
  className,
  ...props
}) => {
  const isOpen = useContext(ToolInvocationOpenContext);

  return (
    <CollapsibleContent
      {...props}
      className={cn(
        "rounded-md border border-border bg-muted p-2 mt-1 text-xs leading-5 text-muted-foreground font-mono whitespace-pre-wrap",
        className
      )}
    >
      {/* `JSON.stringify` — and any child the caller mounts alongside it, such as the
          structured-content sheet — is reached only once the disclosure is open. */}
      {isOpen && (
        <>
          {children}
          <pre className="whitespace-pre-wrap overflow-x-auto ">{JSON.stringify(content, null, 2)}</pre>
        </>
      )}
    </CollapsibleContent>
  );
};

export interface ChatMessageToolInvocationToggleButtonProps extends HTMLAttributes<HTMLDivElement> {}

export const ChatMessageToolInvocationToggleButton: FC<ChatMessageToolInvocationToggleButtonProps> = ({
  className,
  ...props
}) => {
  return (
    <div {...props} className={cn("flex-1 flex justify-end", className)}>
      <Icon
        icon="keyboard_arrow_down"
        className="text-xs text-muted-foreground transition-transform group-data-[state=open]:rotate-180"
      />
    </div>
  );
};
